import memoizeOne from 'memoize-one';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk-service';
import { Country } from '~/Api/Country';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import {
	CzMaxPrice,
	ErrorResponse,
	Product,
	ProductUpdate,
	ValidationResult,
} from '~/Api/User/ProductGroup/CzMaxPrice';
import { handleError } from '~/ErrorHandler';
import { logGoogleTagManagerGroupProductsRemove } from '~/GoogleTagManager';
import { removeFromGroup } from '~/GroupProduct';
import {
	Action as PriceReferencingAction,
	fetchReferenceBasketVersions,
	GroupTypes,
	Module,
	pruneReferenceBasketVersions,
	ReferenceBasketVersion,
	resolveProductGroup,
	selectReferenceBasketVersions,
} from '~/PriceReferencing';
import { isSuklReferenceProduct, PricedProduct, Product as ProductType } from '~/Product';
import { Action as ProductDatasetAction, pruneDatasets } from '~/ProductDataset';
import { Action as ProductPriceAction, pruneAllPrices } from '~/ProductPrice';
import { State, ThunkAction } from '~/Store';

import { Action } from './Action';
import {
	commitCzMaxPriceUpdate,
	fetchCzMaxPrice,
	handleCommitError,
	handleCzMaxPriceUpdateResponse,
	pruneCzMaxPrice,
	scheduleCzMaxPriceUpdate,
} from './actions';
import ProductGroupCzMaxPriceContent from './CzMaxPrice';
import { selectCzMaxPrice, selectExport, Update } from './reducer';
import type { ProductRow } from '~/CzMaxPrice/ProductsTable/ProductsTable';
import { Status } from '~/pages/CzMaxPrice/src/components/ProductParametersPanel';

const compareBasketProducts = (basketProduct: ProductRow, otherBasketProduct: ProductRow): number => {
	if (basketProduct.product !== null && otherBasketProduct.product === null) {
		return -1;
	}
	if (basketProduct.product === null && otherBasketProduct.product !== null) {
		return 1;
	}

	const comparison = basketProduct.country.alpha2.localeCompare(otherBasketProduct.country.alpha2);
	if (comparison !== 0) {
		return comparison;
	}
	if (basketProduct.product !== null && otherBasketProduct.product !== null) {
		return basketProduct.product.product.brandName.localeCompare(otherBasketProduct.product.product.brandName);
	}
	return 0;
};

const groupProducts = memoizeOne(
	(czMaxPrice: CzMaxPrice | null, versions: ReferenceBasketVersion[] | null, showAll: boolean) => {
		const version =
			versions !== null && czMaxPrice !== null
				? versions.find((version) => version.id === czMaxPrice.reference.referenceBasketVersion) ?? null
				: null;
		const countries = version?.referenceCountries ?? [];
		const products = czMaxPrice !== null && version !== null ? czMaxPrice.products : [];
		const nonsetCountries = new Set(countries);
		const usedForCalculationIds = czMaxPrice?.finalCalculation.fromProducts.map((product) => product.id) ?? [];

		const czechProducts: ProductRow[] = [];
		const suklReferencedProducts: ProductRow[] = [];
		const otherProducts: ProductRow[] = [];
		let alternativeProductsCount = 0;

		products.forEach((product) => {
			const group = resolveProductGroup(product.product, countries);
			const isAlternative = isSuklReferenceProduct(product);
			alternativeProductsCount += isAlternative || group.type === GroupTypes.other ? 1 : 0;

			if (!showAll && isAlternative) {
				return;
			}

			const index = usedForCalculationIds.indexOf(product.product.id);
			const lowestPriceIndex = index >= 0 ? index : null;

			if (group.type === GroupTypes.czech) {
				czechProducts.push({
					country: group.country,
					product,
					lowestPriceIndex,
				});
				return;
			}
			if (group.type === GroupTypes.referenced) {
				suklReferencedProducts.push({
					country: group.country,
					product,
					lowestPriceIndex,
				});
				nonsetCountries.delete(group.country);
				return;
			}
			if (showAll) {
				otherProducts.push({
					country: group.country,
					product,
					lowestPriceIndex,
				});
			}
		});

		if (czechProducts.length === 0) {
			czechProducts.push({
				country: new Country('CZ', 'Czech Republic'),
				product: null,
				lowestPriceIndex: null,
			});
		}

		countries.forEach((country) => {
			if (nonsetCountries.has(country)) {
				suklReferencedProducts.push({
					country,
					product: null,
					lowestPriceIndex: null,
				});
			}
		});

		czechProducts.sort(compareBasketProducts);
		suklReferencedProducts.sort(compareBasketProducts);
		otherProducts.sort(compareBasketProducts);

		const pricedProducts = [...czechProducts, ...suklReferencedProducts, ...otherProducts]
			.map((basketProduct) => {
				const { product } = basketProduct;
				return product !== null ? new PricedProduct(product, product.product, null) : null;
			})
			.filter((pricedProduct) => pricedProduct !== null) as Array<PricedProduct<Product>>;

		return {
			pricedProducts,
			czechProducts,
			suklReferencedProducts,
			otherProducts,
			alternativeProductsCount,
			referenceBasketVersion: version,
		};
	}
);

const selectUpdateStatus = memoizeOne((groupId: string, update: Update | null) => {
	if (update?.groupId !== groupId) {
		return null;
	}

	switch (update.state.name) {
		case 'scheduled':
		case 'fetching':
			return Status.saving;
		case 'updated':
			return Status.saved;
		case 'failed':
			return Status.failed;
		default:
			return null;
	}
});

function mapStateToProps(
	state: State,
	{ group }: { group: UserProductGroup }
): {
	pricedProducts: PricedProduct<Product>[];
	czechProducts: ProductRow[];
	suklReferencedProducts: ProductRow[];
	otherProducts: ProductRow[];
	czMaxPrice: CzMaxPrice | null;
	versions: ReferenceBasketVersion[] | null;
	validationResult: ValidationResult | null;
	updateStatus: Status | null;
	isFetching: boolean;
} & ReturnType<typeof groupProducts> {
	const czMaxPriceState = selectCzMaxPrice(state.czMaxPrice, group.id);
	const czMaxPrice = czMaxPriceState?.czMaxPrice ?? null;
	const versions: ReferenceBasketVersion[] | null = selectReferenceBasketVersions(
		state.priceReferencing,
		Module.czMaxPrice
	);

	return {
		...groupProducts(czMaxPrice, versions, state.products.showAllProducts),
		czMaxPrice,
		versions,
		validationResult: selectExport(state.czMaxPrice, group.id)?.validationResult ?? null,
		updateStatus: selectUpdateStatus(group.id, state.czMaxPrice.update),
		isFetching: Boolean(czMaxPriceState?.isFetching),
	};
}

type PruneAction = Action | PriceReferencingAction | ProductPriceAction | ProductDatasetAction;
type Dispatch = ThunkDispatch<State, never, Action>;

const actions = {
	onFetch:
		(group: UserProductGroup): ThunkAction<Promise<void>, Action> =>
		async (dispatch) => {
			dispatch(fetchReferenceBasketVersions(Module.czMaxPrice));

			try {
				await dispatch(commitCzMaxPriceUpdate(group.id));
			} catch (error) {
				dispatch(handleCommitError(error));
			}

			try {
				await dispatch(fetchCzMaxPrice(group.id));
			} catch (error) {
				if (!(error instanceof ErrorResponse)) {
					dispatch(handleError(error));
					return;
				}
				error.handleProductGroupNotFound();
				dispatch(handleError(error));
			}
		},
	onPrune:
		(group: UserProductGroup): ThunkAction<Promise<void>, PruneAction> =>
		async (dispatch) => {
			try {
				await dispatch(commitCzMaxPriceUpdate(group.id));
			} catch (error) {
				dispatch(handleCommitError(error));
			}
			dispatch(pruneCzMaxPrice(group.id));
			dispatch(pruneReferenceBasketVersions());
			dispatch(pruneAllPrices());
			dispatch(pruneDatasets());
		},
	onUpdate:
		(
			group: UserProductGroup,
			packageSize: number | null,
			referenceBasketVersion: string,
			products: ProductUpdate[]
		): ThunkAction<Promise<void>, Action> =>
		async (dispatch) => {
			try {
				await dispatch(scheduleCzMaxPriceUpdate(group.id, packageSize, referenceBasketVersion, products));
			} catch (error) {
				dispatch(handleCzMaxPriceUpdateResponse(error, group.id));
			}
		},
	onSave:
		(group: UserProductGroup): ThunkAction<Promise<void>, Action> =>
		async (dispatch) => {
			try {
				await dispatch(commitCzMaxPriceUpdate(group.id));
			} catch (error) {
				dispatch(handleCzMaxPriceUpdateResponse(error, group.id));
			}
		},
	onDeleteItems:
		(group: UserProductGroup, products: ProductType[], onSuccess: () => void) => async (dispatch: Dispatch) => {
			try {
				await dispatch(removeFromGroup(group.id, products));
				logGoogleTagManagerGroupProductsRemove(group.id, products);
				if (onSuccess !== undefined) {
					onSuccess();
				}
			} catch (error) {
				dispatch(handleError(error));
			}
		},
};

export default connect(mapStateToProps, actions)(ProductGroupCzMaxPriceContent);
